/**
 * A custom element for handling product form submissions and cart updates.
 * This component manages the 'Add to Cart' process via AJAX,
 * updating the UI without a full page reload.
 *
 * @class ProductForm
 * @extends HTMLElement
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // Find the parent product section to get context
        this.productParentElement = this.closest('[data-section][data-product]');
        this.dataset.section = this.productParentElement.dataset.section;
        this.dataset.product = this.productParentElement.dataset.product;

        this.form = this.querySelector('form');
        this.form.querySelector('[name="id"]').disabled = false; // Enable the variant ID input

        this.informationModal = document.querySelector('information-modal');
        this.atcButtons = this.productParentElement.querySelectorAll('product-buttons [type="submit"]');
      }

      /**
       * Called when the element is added to the document's DOM.
       */
      connectedCallback() {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Initialize Shopify's dynamic payment buttons if they exist
        Shopify?.PaymentButton?.init();
      }

      /**
       * Handles the form submission event.
       * @param {Event} evt - The submit event.
       */
      onSubmitHandler(evt) {
        evt.preventDefault();

        // Prevent submission if no variant is selected
        if (this.productParentElement.dataset.variantSelected === 'false') {
          this.handleErrorMessage(window.variantStrings.selectVariant);
          return;
        }

        this.handleAtcButtonStates('loading');
        this.handleErrorMessage(); // Clear previous error messages

        const config = fetchConfig('javascript'); // Assumes a global fetchConfig helper function
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Find all dynamic sections (like the cart drawer/icon) that need updating
        this.dynamicSectionsNodes = document.querySelectorAll('cart-dynamic');
        if (this.dynamicSectionsNodes.length > 0) {
          const dynamicSectionIds = [...this.dynamicSectionsNodes].map((item) => item.dataset.sectionId);
          formData.append('sections', dynamicSectionIds.join(','));
        }

        config.body = formData;

        // AJAX 'Add to Cart' request
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Handle Shopify API errors (e.g., inventory issues)
              let errorMessage = response.description || 'An error occurred.';
              this.handleErrorMessage(errorMessage);
              this.handleAtcButtonStates('error');
              return;
            }

            // Success! Update dynamic sections with new HTML from the response.
            this.dynamicSectionsNodes.forEach((node) => {
              const htmlString = response.sections[node.dataset.sectionId];
              if (htmlString) {
                const html = new DOMParser().parseFromString(htmlString, 'text/html');
                const newContent = html.querySelector(`cart-dynamic[data-section-id='${node.dataset.sectionId}']`);
                if (newContent) {
                  node.innerHTML = newContent.innerHTML;
                  node.className = newContent.className; // Sync classes
                  if (typeof node.modified === 'function') node.modified(); // Optional callback
                }
              }
            });

            this.handleAtcButtonStates('success');

            // 1. Close Quick View Modal (Prioritized)
            // Attempt to close via standard modal-dialog or the user-suggested quick-add-modal
            const modal = this.closest('modal-dialog, quick-add-modal');
            if (modal) {
              if (typeof modal.hide === 'function') modal.hide();
              else if (typeof modal.close === 'function') modal.close();
              else modal.removeAttribute('open'); // Fallback
            }

            // 2. Open Cart Drawer (Delayed slightly for animation smoothness)
            setTimeout(() => {
              const cartDrawer = document.querySelector('drawer-element#cart-drawer');
              if (cartDrawer && cartDrawer.openDrawer) {
                cartDrawer.openDrawer();
              } else {
                const drawerTrigger = document.querySelector('drawer-trigger[data-target="cart-drawer"]');
                if (drawerTrigger) drawerTrigger.click();
              }
            }, 300); // 300ms matches standard transition times
          })
          .catch((e) => {
            console.error(e);
            this.handleAtcButtonStates('error');
          });
      }

      /**
       * Updates the state of all 'Add to Cart' buttons.
       * @param {'loading' | 'error' | 'success'} state - The new button state.
       */
      handleAtcButtonStates(state) {
        this.atcButtons.forEach((button) => {
          switch (state) {
            case 'loading':
              button.setAttribute('aria-disabled', true);
              button.dataset.state = 'loading';
              break;

            case 'error':
              button.dataset.state = 'error';
              button.removeAttribute('aria-disabled');
              navigator.vibrate?.(100);
              setTimeout(() => button.removeAttribute('data-state'), 3000);
              break;

            case 'success':
              button.dataset.state = 'success';
              button.removeAttribute('aria-disabled');
              navigator.vibrate?.(100);
              setTimeout(() => button.removeAttribute('data-state'), 3000);
              break;
          }
        });
      }

      /**
       * Displays or hides an error message.
       * @param {string | boolean} errorMessage - The message to display, or `false` to hide.
       */
      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrappers =
          this.errorMessageWrappers || this.productParentElement.querySelectorAll('error-message');

        this.errorMessageWrappers.forEach((wrapper) => {
          if (errorMessage) {
            wrapper.querySelector('span').textContent = errorMessage;
            wrapper.classList.remove('hidden');
            setTimeout(() => wrapper.classList.add('hidden'), 3000);
          } else {
            wrapper.classList.add('hidden');
          }
        });
      }
    }
  );
}